/**
 * @module app.shared
 */

export type EssetteLogLevel = 'error' | 'warn' | 'info' | 'debug';
export type LogTarget = 'default' | 'server' | 'console' | 'all';
export type LogPriority = 'normal' | 'high';

export interface ILogItem {
    timestamp: string;
    message: string;
    level: EssetteLogLevel;
    source: string;
    target: LogTarget;
    trace: string;
    style: string;
    priority: LogPriority;
    url: string;
    error: Error | null;
    details: any;
}

export interface ILogDefaults {
    source: any;
    target: LogTarget;
    priority: LogPriority;
    trace: string;
    style: string;
}

/**
 * **NOTE**: This class name is intentionally short so that it does not take up much room when written to the development `console`.
 */
export class LogItem implements Readonly<ILogItem> {

    public readonly timestamp: string;
    public readonly source: string;
    public readonly trace: string;
    public readonly style: string;
    public readonly message: string;
    public readonly level: EssetteLogLevel;
    public readonly priority: LogPriority;
    public readonly url: string;
    public readonly error: Error | null;
    public readonly details: any;
    public readonly target: LogTarget;

    constructor(item: Readonly<ILogItem>) {
        // this order is what we want to show up in the log
        this.timestamp = item.timestamp;
        this.source = item.source;
        this.trace = item.trace;
        this.style = item.style;
        this.message = item.message;
        this.level = item.level;
        this.priority = item.priority;
        this.url = item.url;
        this.error = item.error;
        this.details = item.details;
        this.target = item.target;
    }

    public toString(): string {
        return `[${this.level}: ${this.source}] ${this.message}`;
    }

    public isDuplicate(other: LogItem): boolean {
        return (this === other) || (
            this.message === other.message &&
            this.level === other.level &&
            this.source === other.source &&
            this.url === other.url);
    }

    public static create(item: Partial<ILogItem>, defaults?: Partial<ILogDefaults>): LogItem {
        const timestamp = item.timestamp || LogItem.getTimestamp();
        const sourceString = item.source || LogItem.getSourceString(defaults && defaults.source);
        const trace = item.trace || (defaults && defaults.trace) || '';
        const style = item.style || (defaults && defaults.style) || '';
        const target = item.target || (defaults && defaults.target) || 'default';
        const priority = item.priority || (defaults && defaults.priority) || 'normal';
        const message = item.message || (item.error && item.error.message) || `[${timestamp}] ${sourceString}`;

        let populated: ILogItem = {
            timestamp,
            message,
            level: item.level || 'info',
            source: sourceString,
            target,
            trace,
            style,
            priority,
            url: item.url || window.location.href,
            error: item.error || null,
            details: item.details
        };

        return new LogItem(populated);
    }

    public static getTimestamp(): string {
        return new Date(Date.now()).toJSON();
    }

    private static getSourceString(source: any): string {
        if(source !== undefined && source !== null) {
            switch(typeof (source)) {
                case 'string':
                    return source;
                case 'object':
                    return (source as Object).constructor.name;
                case 'function':
                    return (source as Function).name;
                default:
                    return String(source);
            }
        }
        return window.location.hash || 'unknown';
    }

}
